import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import debounce from "lodash/debounce"
import { useTranslation } from "next-i18next"
import { selectGenericState, showToast } from "@/store/features/genericSlice"
import { createContextLogger } from "@/utils/logger/client"

import QuantitySelector from "@/components/cart/QuantitySelector"
import { getStock } from "@/utils/cart"
import { getAnalyticsData } from "@/components/ProductDetail/v3/AddToCart/analytics"

import Button from "@/components/core/Button/Button"

const AddToCartActions = props => {
  const {
    cart = {},
    onQtyChange = () => {},
    onAddToCart = () => {},
    isComboSku = false,
    customerBrandName = "",
    sku = "",
    price = 0,
    backOrder = "",
    data: {
      setStockAlert,
      setProductStock,
      addToCartButton,
      productAnalyticsData = {},
      addProductToCart,
      addToCartTxt,
      isBundleProduct,
    } = {},
    productData: { name: productName = "" } = {},
  } = props || {}

  const logger = createContextLogger("pdp:add-to-cart:actions")

  const dispatch = useDispatch()
  const [quantity, setQuantity] = useState(1)
  const [loading, setLoading] = useState(false)
  const { featureFlags, globalConfig = {} } = useSelector(selectGenericState)

  const { MYSQLCONNSTR_DISABLE_ADD_CART: disableAddcart } = featureFlags
  const { apiEndpoints: { stockFailureErrorMsg = "" } = {} } = globalConfig

  const { t } = useTranslation("common")

  const handleChange = qty => {
    setStockAlert({ show: false, mssg: "" })
    if (backOrder === "Yes") {
      onQtyChange(qty)
      setQuantity(qty)
      setLoading(false)
      return
    }
    onQtyChange(qty)
    setLoading(true)
    const cartItem =
      cart.lineItems?.find(item => item.variant.sku === sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0
    if (qty && qty > 0) {
      setQuantity(qty)
      getStock(sku)
        .then(({ stock = 0 }) => {
          if (cartQuantity > 0 && stock <= cartQuantity) {
            setStockAlert({
              show: true,
              mssg: t("kf.pdp.label.noItemsToShip"),
            })
          } else if (stock >= qty + cartQuantity) {
            onQtyChange(qty)
          } else {
            setProductStock(stock)
            if (stock > 0) {
              setQuantity(stock - cartQuantity)
              onQtyChange(stock - cartQuantity)
              setStockAlert({
                show: true,
                mssg: t("kf.pdp.label.availableToShip", { qty: stock }),
              })
            }
          }
          setLoading(false)
        })
        .catch(error => {
          logger.error("Error fetching stock for quantity change", {
            error,
            sku,
          })
          setLoading(false)
        })
    }
  }

  const handleATCKeydown = e => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      window.previewCartKeySource = true
    } else {
      window.previewCartKeySource = false
    }
  }
  const onAddToCartCb = () => {
    setLoading(false)
  }

  const handleAddToCart = () => {
    setLoading(true)
    setStockAlert({ show: false, mssg: "" })
    if (backOrder === "Yes") {
      onAddToCart(
        [
          {
            sku,
            quantity,
            isBundleProduct,
            isBundle: isComboSku,
            backorder: true,
            brand: customerBrandName?.toLowerCase(),
          },
        ],
        onAddToCartCb
      )

      return
    }

    window.cartButtonElement = addToCartButton.current.firstElementChild
    const cartItem =
      cart.lineItems?.find(item => item.variant.sku === sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0

    getStock(sku)
      .then((data = {}) => {
        const { stock = 0, isError = false } = data
        if (isError) {
          dispatch(
            showToast({
              isVisible: true,
              message: stockFailureErrorMsg,
            })
          )
        }

        setLoading(false)
        setProductStock(stock)
        if (cartQuantity + quantity > stock) {
          if (stock > cartQuantity) {
            onAddToCart(
              [
                {
                  sku,
                  isBundleProduct,
                  quantity: stock - cartQuantity,
                  brand: customerBrandName?.toLowerCase(),
                },
              ],
              onAddToCartCb
            )
            setStockAlert({
              show: true,
              mssg: t("kf.pdp.label.availableToShip", { qty: stock }),
            })
          } else {
            setStockAlert({
              show: true,
              mssg: t("kf.pdp.label.noItemsToShip"),
            })
          }
        } else {
          onAddToCart(
            [
              {
                sku,
                quantity,
                isBundleProduct,
                isBundle: isComboSku,
                brand: customerBrandName?.toLowerCase(),
              },
            ],
            onAddToCartCb
          )
        }
      })
      .catch(error => {
        logger.error("Error fetching stock for add to cart", { error, sku })
        setLoading(false)
      })
  }

  return (
    <div className="add-cart-container">
      <div className="quantity-selector-container">
        <QuantitySelector
          productData={productAnalyticsData}
          value={quantity}
          onChange={handleChange}
          loading={loading}
          isPDPQuantitySelector={true}
          doNotRequiredAnalyticsData={true}
          isAddToCart={true}
        />
      </div>
      <div className="add-to-cart-container" ref={addToCartButton}>
        <Button
          role="button"
          ariaLabel={addProductToCart(productName)}
          className="gbh-data-layer"
          data-gbh-data-layer={getAnalyticsData(
            addToCartTxt,
            "",
            "",
            isBundleProduct
          )}
          data-gbh-data-layer-custom={productAnalyticsData.stringProductInfo}
          label={addToCartTxt}
          onClick={debounce(handleAddToCart, 500)}
          onKeyDown={handleATCKeydown}
          size="large"
          type="primary"
          flexible={true}
          disabled={
            loading ||
            !quantity ||
            parseInt(quantity) < 1 ||
            !price ||
            disableAddcart
          }
          loading={loading}
        />
      </div>
    </div>
  )
}

export default AddToCartActions
